import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Badge, Box, IconButton, Stack, Tooltip, Typography} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import EditNoteIcon from '@mui/icons-material/EditNote';
import {useAssistant} from '../AssistantPanelContext';
import {useTranslation} from 'react-i18next';

import {
    ASSISTANT_PANEL_WIDTH,
    ASSISTANT_PANEL_WIDTH_EXPANDED,
    ASSISTANT_DOCK_MIN_WIDTH_PX,
    ASSISTANT_FAB_MARGIN,
    ASSISTANT_FAB_MOBILE_MARGIN,
    ASSISTANT_FAB_MOBILE_SIZE,
    ASSISTANT_FAB_SIZE,
    ASSISTANT_PANEL_GAP,
} from '../constants';
import {useAssistantChat} from '../chat/useAssistantChat';
import AssistantChat from '../chat/AssistantChat';
import AssistantConversations from '../conversations/AssistantConversations';
import {RagThreadService} from 'crqlar-react-common/dist/services/ai/ragThread.service';
import {
    getUnreadTeamThreadIds,
    hasPendingHandoff as hasPendingHandoffOnThread,
    hasUnreadTeamReply,
} from '../assistantThreadState';

// persisted expand-mode preference
const EXPANDED_STORAGE_KEY = 'assistant.expanded';

// The active transcript has its own 5s poll. This lighter list check covers every other
// conversation and keeps the closed launcher notification reasonably prompt.
const THREAD_LIST_POLL_INTERVAL_MS = 15_000;

/**
 * Panel shell — handles:
 *  - fixed positioning above the global toggle, slide-in/out and width changes
 *  - the header bar (menu / title / expand / close)
 *  - switching between the chat view and the conversations browser
 *
 * The actual chat (messages, composer, RAG api calls) and the
 * conversations list (listThreads) each live in their own
 * components; this file just composes them.
 */
const AssistantPanel: React.FC = () => {
    const {t} = useTranslation();
    const assistant = useAssistant();
    const isOpen = !!assistant?.isOpen;

    const [isExpanded, setIsExpanded] = useState<boolean>(() => {
        try {
            return localStorage.getItem(EXPANDED_STORAGE_KEY) === 'true';
        } catch {
            return false;
        }
    });
    useEffect(() => {
        try {
            localStorage.setItem(EXPANDED_STORAGE_KEY, isExpanded ? 'true' : 'false');
        } catch {
            // ignore quota / privacy mode errors
        }
    }, [isExpanded]);

    const [showConversations, setShowConversations] = useState(false);
    const [composerFocusToken, setComposerFocusToken] = useState(0);
    const [hasUnreadAnswer, setHasUnreadAnswer] = useState(false);
    // Ordered newest-first. The first id is the conversation opened from the
    // closed-panel reply notification; the full set also marks rows in History.
    const [unreadThreadIds, setUnreadThreadIds] = useState<string[]>([]);
    const lastSeenAnsweredSeqByThreadRef = useRef<Map<string, number>>(new Map());

    // Conversation state + actions live in a hook so the conversations
    // list can trigger a load without needing to know how the chat works.
    const chat = useAssistantChat({pollingEnabled: isOpen});
    const activeThreadId = chat.threadId;
    const activeThread = chat.thread;
    const chatMessages = chat.messages;
    const markChatRead = chat.markRead;
    const setAssistantStatus = assistant?.setStatus;
    // loadThread isn't memoised; keep a ref so the open-to-thread effect
    // can call the latest without re-subscribing every render.
    const loadThreadRef = useRef(chat.loadThread);
    loadThreadRef.current = chat.loadThread;
    const newestUnreadThreadId = unreadThreadIds[0] ?? null;
    const newestUnreadThreadIdRef = useRef<string | null>(newestUnreadThreadId);
    newestUnreadThreadIdRef.current = newestUnreadThreadId;

    const currentWidth = isExpanded
        ? ASSISTANT_PANEL_WIDTH_EXPANDED
        : ASSISTANT_PANEL_WIDTH;
    // The thread's handoff status is the whole truth about who is answering — the server routes on it
    // and stamps it onto every send and poll response, so the widget only ever renders it.
    const handoffStatus = activeThread?.handoffStatus;
    // Same rule as the conversation list: pending = handed over, no reply yet.
    const hasPendingHandoff = activeThread ? hasPendingHandoffOnThread(activeThread) : false;

    // Session-scoped memory of conversations we've observed to have a
    // pending handoff in their loaded transcript. The list payload's
    // own handoffStatus can lag behind reality, so once we've seen
    // the truth for a given conversation, we keep that knowledge until
    // we see the same conversation again with no pending messages.
    const [observedPendingIds, setObservedPendingIds] = useState<Set<string>>(
        () => new Set(),
    );
    useEffect(() => {
        const id = chat.threadId;
        if (!id) return;
        // Only update once we've actually loaded the conversation —
        // otherwise an empty messages array during the bootstrap window
        // would falsely "clear" a conversation we just opened.
        if (chat.isLoading) return;
        setObservedPendingIds(curr => {
            const isPending = hasPendingHandoff;
            const has = curr.has(id);
            if (isPending && !has) {
                const next = new Set(curr);
                next.add(id);
                return next;
            }
            if (!isPending && has) {
                const next = new Set(curr);
                next.delete(id);
                return next;
            }
            return curr;
        });
    }, [chat.threadId, chat.isLoading, hasPendingHandoff]);
    const latestAnsweredSeq = useMemo(
        () => chatMessages.reduce<number | undefined>((latest, message) => {
            if (message.role !== 'assistant' || message.status !== 'ANSWERED') return latest;
            if (message.seq === undefined) return latest;
            return latest === undefined
                ? message.seq
                : Math.max(latest, message.seq);
        }, undefined),
        [chatMessages],
    );
    const hasServerUnreadAnswer =
        latestAnsweredSeq !== undefined &&
        latestAnsweredSeq > (activeThread?.userLastReadSeq ?? -1);
    const activeHasUnreadTeamReply = activeThread ? hasUnreadTeamReply(activeThread) : false;
    const visibleUnreadThreadIds =
        activeHasUnreadTeamReply && activeThreadId && !unreadThreadIds.includes(activeThreadId)
            ? [activeThreadId, ...unreadThreadIds]
            : unreadThreadIds;
    const shouldShowUnreadAnswer = !isOpen && (
        hasUnreadAnswer
        || hasServerUnreadAnswer
        || activeHasUnreadTeamReply
        || unreadThreadIds.length > 0
    );
    const hasUnreadOtherThread = unreadThreadIds.some(id => id !== activeThreadId);

    useEffect(() => {
        if (!activeThreadId || latestAnsweredSeq === undefined) return;

        const lastSeen = lastSeenAnsweredSeqByThreadRef.current.get(activeThreadId);
        if (lastSeen === undefined || latestAnsweredSeq > lastSeen) {
            if (!isOpen && lastSeen !== undefined) {
                setHasUnreadAnswer(true);
            }
            lastSeenAnsweredSeqByThreadRef.current.set(activeThreadId, latestAnsweredSeq);
        }
    }, [activeThreadId, isOpen, latestAnsweredSeq]);

    useEffect(() => {
        if (isOpen) {
            setHasUnreadAnswer(false);
        }
    }, [isOpen]);

    // Check every thread in every panel state. The active transcript poll cannot discover a
    // reply to an older conversation, and stopping this check while open made those replies
    // invisible. `check` no-ops in a hidden tab; focus/visibility catches up immediately.
    useEffect(() => {
        let cancelled = false;
        let checking = false;
        const check = async () => {
            if (document.hidden || checking) return;
            checking = true;
            try {
                const page = await RagThreadService.listThreads({limit: {limit: 50, offset: 0}});
                if (cancelled) return;
                setUnreadThreadIds(getUnreadTeamThreadIds(page.content ?? []));
            } catch {
                // best-effort; the next focus retries
            } finally {
                checking = false;
            }
        };
        void check();
        window.addEventListener('focus', check);
        document.addEventListener('visibilitychange', check);
        const interval = window.setInterval(check, THREAD_LIST_POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            window.clearInterval(interval);
            window.removeEventListener('focus', check);
            document.removeEventListener('visibilitychange', check);
        };
    }, []);

    // Opening the reply notification always fetches a fresh snapshot, including when the
    // unread conversation is already active. Keeping the old cached snapshot in that case
    // was the path that forced users through New chat -> History -> conversation.
    useEffect(() => {
        if (!isOpen) return;
        const target = newestUnreadThreadIdRef.current;
        if (!target) return;

        let cancelled = false;
        setShowConversations(false);
        void loadThreadRef.current(target).then(loaded => {
            if (!cancelled && loaded) {
                setUnreadThreadIds(curr => curr.filter(id => id !== target));
            }
        });
        return () => {
            cancelled = true;
        };
    }, [isOpen]);

    useEffect(() => {
        if (!setAssistantStatus) return;

        // Don't map `isLoading` to "asking" — it covers bootstrap +
        // conversation-fetch, which produces a misleading blue dot for
        // a split second on page reload before the real pending state
        // (orange) takes over. Only an in-flight ask is "asking".
        setAssistantStatus(
            shouldShowUnreadAnswer
                ? 'unreadAnswer'
                : chat.isAsking
                    ? 'asking'
                    : hasPendingHandoff
                        ? 'waitingForHuman'
                        : 'idle',
        );
    }, [chat.isAsking, hasPendingHandoff, setAssistantStatus, shouldShowUnreadAnswer]);

    useEffect(() => {
        return () => setAssistantStatus?.('idle');
    }, [setAssistantStatus]);

    useEffect(() => {
        if (isOpen && !showConversations) {
            setComposerFocusToken(token => token + 1);
        }
    }, [isOpen, showConversations]);

    useEffect(() => {
        if (!isOpen || showConversations || !activeThreadId) return;

        const latestSeq = chatMessages.reduce<number | undefined>(
            (latest, message) => {
                if (message.seq === undefined) return latest;
                return latest === undefined
                    ? message.seq
                    : Math.max(latest, message.seq);
            },
            undefined,
        );
        void markChatRead(latestSeq);
    }, [isOpen, showConversations, activeThreadId, chatMessages, markChatRead]);

    if (!assistant) return null;

    const handleSelectConversation = async (id: string) => {
        const loaded = await chat.loadThread(id);
        if (loaded) {
            setUnreadThreadIds(curr => curr.filter(threadId => threadId !== id));
        }
        setShowConversations(false);
        setComposerFocusToken(token => token + 1);
    };

    const handleStartNew = () => {
        chat.startNew();
        setShowConversations(false);
        setComposerFocusToken(token => token + 1);
    };

    const handleConversationDeleted = (id: string) => {
        if (id === chat.threadId) {
            chat.startNew();
        }
    };

    return (
            <Box
                role="dialog"
                aria-label={t('Crqlar Assistant')}
                aria-hidden={!isOpen}
                className="hide-on-print"
                data-assistant-panel-root
                sx={{
                    position: 'fixed',
                    top: {
                        xs: `calc(var(--banner-offset, 0px) + ${ASSISTANT_FAB_MOBILE_MARGIN}px)`,
                        sm: `calc(var(--banner-offset, 0px) + ${ASSISTANT_FAB_MARGIN}px)`,
                    },
                    right: {
                        xs: `${ASSISTANT_FAB_MOBILE_MARGIN}px`,
                        sm: `${ASSISTANT_FAB_MARGIN}px`,
                    },
                    bottom: {
                        xs: `${ASSISTANT_FAB_MOBILE_MARGIN + ASSISTANT_FAB_MOBILE_SIZE + ASSISTANT_PANEL_GAP}px`,
                        sm: `${ASSISTANT_FAB_MARGIN + ASSISTANT_FAB_SIZE + ASSISTANT_PANEL_GAP}px`,
                    },
                    width: {
                        xs: `calc(100vw - ${ASSISTANT_FAB_MOBILE_MARGIN * 2}px)`,
                        sm: `min(${ASSISTANT_PANEL_WIDTH}px, calc(100vw - ${ASSISTANT_FAB_MARGIN * 2}px))`,
                    },
                    borderRadius: '12px',
                    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.14)',
                    border: '1px solid rgba(0, 0, 0, 0.08)',

                    [`@media (min-width: ${ASSISTANT_DOCK_MIN_WIDTH_PX}px)`]: {
                        width: `${currentWidth}px`,
                        transition: 'transform 250ms ease, width 250ms ease',
                    },

                    bgcolor: '#fff',
                    display: 'flex',
                    flexDirection: 'column',
                    zIndex: 1250,
                    overflow: 'hidden',
                    transform: isOpen
                        ? 'translateX(0)'
                        : {
                            xs: `translateX(calc(100% + ${ASSISTANT_FAB_MOBILE_MARGIN}px))`,
                            sm: `translateX(calc(100% + ${ASSISTANT_FAB_MARGIN}px))`,
                        },
                    transition: 'transform 250ms ease',
                    pointerEvents: isOpen ? 'auto' : 'none',
                }}
            >
                {/* Header bar */}
                <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                        px: 1.5,
                        py: 1.25,
                        borderBottom: '1px solid #e0e0e0',
                        minHeight: 56,
                    }}
                >
                    <Tooltip
                        title={showConversations ? t('Back to chat') : t('View history')}
                    >
                        <IconButton
                            onClick={() => setShowConversations(v => !v)}
                            size="small"
                            aria-pressed={showConversations}
                            aria-label={
                                showConversations
                                    ? t('Back to chat')
                                    : t('Show previous conversations')
                            }
                            sx={{mr: 0.5}}
                        >
                            <Badge
                                color="error"
                                variant="dot"
                                invisible={!hasUnreadOtherThread}
                                overlap="circular"
                            >
                                {showConversations
                                    ? <ArrowBackIcon fontSize="small"/>
                                    : <MenuIcon fontSize="small"/>}
                            </Badge>
                        </IconButton>
                    </Tooltip>

                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.75}
                        sx={{flex: 1, minWidth: 0}}
                    >
                        <Typography
                            variant="subtitle1"
                            fontWeight={600}
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {showConversations ? t('History') : t('Crqlar Assistant')}
                        </Typography>

                    </Stack>

                    <Stack direction="row" spacing={0.5} alignItems="center">
                        {/* New-conversation shortcut — only shows in the
                            chat view once there's something to start
                            fresh from. */}
                        {!showConversations && chat.messages.length > 0 && (
                            <Tooltip title={t('New conversation')}>
                                <IconButton
                                    onClick={handleStartNew}
                                    size="small"
                                    aria-label={t('Start a new conversation')}
                                >
                                    <EditNoteIcon fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                        )}

                        <Tooltip title={isExpanded ? t('Collapse panel') : t('Expand panel')}>
                            <IconButton
                                onClick={() => setIsExpanded(v => !v)}
                                size="small"
                                aria-pressed={isExpanded}
                                aria-label={isExpanded ? t('Collapse panel') : t('Expand panel')}
                                sx={{
                                    // only meaningful when docked — in overlay
                                    // mode the panel is already full-width
                                    display: 'none',
                                    [`@media (min-width: ${ASSISTANT_DOCK_MIN_WIDTH_PX}px)`]: {
                                        display: 'inline-flex',
                                    },
                                    transform: isExpanded ? 'scaleX(-1)' : 'none',
                                }}
                            >
                                <ViewSidebarOutlinedIcon fontSize="small"/>
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={t('Close')}>
                            <IconButton
                                onClick={assistant.close}
                                size="small"
                                aria-label={t('Close assistant')}
                            >
                                <CloseIcon fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>

                {/* Body */}
                {showConversations ? (
                    <AssistantConversations
                        currentConversationId={chat.threadId}
                        onSelect={handleSelectConversation}
                        onStartNew={handleStartNew}
                        onDeleted={handleConversationDeleted}
                        // Bridge: list payload's handoff fields can lag
                        // behind reality. We accumulate session-observed
                        // pending conversations (any we've opened and
                        // confirmed via the loaded transcript) and pass
                        // them through, so the dot stays correct even
                        // after the user switches away.
                        extraPendingConversationIds={Array.from(observedPendingIds)}
                        extraUnreadConversationIds={visibleUnreadThreadIds}
                    />
                ) : (
                    <AssistantChat
                        messages={chat.messages}
                        isAsking={chat.isAsking}
                        isLoading={chat.isLoading}
                        onSubmit={chat.submit}
                        onFeedback={chat.submitFeedback}
                        onDelete={chat.deleteMessage}
                        onRetry={chat.regenerateAnswer}
                        onEdit={chat.editMessage}
                        onAskTeam={chat.requestHandoff}
                        onBackToAssistant={chat.endHandoff}
                        onStartNewChat={handleStartNew}
                        onStop={chat.cancelAsk}
                        streamingMessageId={chat.streamingMessageId}
                        conversationId={chat.threadId}
                        handoffStatus={handoffStatus}
                        shouldFocusComposer={isOpen}
                        composerFocusToken={composerFocusToken}
                    />
                )}
            </Box>
    );
};

export default AssistantPanel;
